/**
 * Shadow session (QA "view as role") client service.
 *
 * Calls the backend to mint a short-lived, read-only token that impersonates a
 * chosen role scoped to a study/site, then swaps it in for the tab so every
 * permission check and data query is enforced by the real backend as that role.
 * The real token is stashed and restored on exit.
 *
 * See flexcoa-api/flexcoa_api/utils/shadow_session.py for the backend contract.
 */
import api from './api';

const REAL_TOKEN_KEY = 'authToken_real';

/**
 * Infer the study/site scope from the current URL so a persona is automatically
 * scoped to whatever the tester is looking at. Route shape:
 *   /studies/:studyId/sites/:siteId/...
 */
export function detectScopeFromUrl(pathname = window.location.pathname) {
  const studyMatch = pathname.match(/\/studies\/([0-9a-f-]{36})/);
  const siteMatch = pathname.match(/\/sites\/([0-9a-f-]{36})/);
  return {
    studyId: studyMatch ? studyMatch[1] : null,
    siteId: siteMatch ? siteMatch[1] : null,
  };
}

/**
 * Mint a shadow token for the given role + scope and swap it in. Must be called
 * while the real (internal) token is still active. Returns the backend payload.
 */
export async function startShadowSession({ role, studyId = null, siteId = null }) {
  const resp = await api.post('/api/dev/shadow-session/', {
    role,
    study_id: studyId,
    site_id: siteId,
  });
  const accessToken = resp.data?.access_token;
  if (!accessToken) throw new Error('Shadow session did not return a token');

  // Stash the real token, then swap in the shadow token. The axios interceptor
  // reads localStorage.authToken, so this transparently re-auths the tab.
  const realToken = localStorage.getItem('authToken');
  if (realToken) localStorage.setItem(REAL_TOKEN_KEY, realToken);
  localStorage.setItem('authToken', accessToken);

  return resp.data;
}

/** Restore the real token and drop the shadow token. */
export function endShadowSession() {
  const realToken = localStorage.getItem(REAL_TOKEN_KEY);
  if (realToken) {
    localStorage.setItem('authToken', realToken);
    localStorage.removeItem(REAL_TOKEN_KEY);
  }
}

/** True if a shadow token is currently swapped in (real token is stashed). */
export function isShadowActive() {
  return !!localStorage.getItem(REAL_TOKEN_KEY);
}
